import { useState, useEffect, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowRight, CalendarIcon, Loader2 } from "lucide-react";

const Calendar = lazy(() => import("@/components/ui/calendar").then(m => ({ default: m.Calendar })));
import { SocialProofTicker } from "@/components/SocialProofTicker";
import { Link } from "react-router-dom";
import { format, addDays } from "date-fns";
import { cn } from "@/lib/utils";
import { calculateOfferAmount } from "@/lib/fees";
const heroHouse = "/images/hero-house.jpg";

type UserType = "agent" | "broker";
type HeroState = "input" | "loading" | "result";

export { SocialProofTicker };

export const Hero = () => {
  const [userType, setUserType] = useState<UserType>("agent");
  const [highlightFields, setHighlightFields] = useState(false);
  const [commissionValue, setCommissionValue] = useState("");
  const [completionDate, setCompletionDate] = useState<Date>();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [heroState, setHeroState] = useState<HeroState>("input");
  const [offerAmount, setOfferAmount] = useState(0);

  const calculateOffer = () => {
    const value = parseFloat(commissionValue.replace(/,/g, ""));
    if (!value || !completionDate) return;

    setHeroState("loading");

    setTimeout(() => {
      const exchangeDateStr = format(new Date(), "yyyy-MM-dd");
      const completionDateStr = format(completionDate, "yyyy-MM-dd");
      const offer = calculateOfferAmount(value, exchangeDateStr, completionDateStr, "a");
      setOfferAmount(offer);
      setHeroState("result");
    }, 1500);
  };

  const handleCommissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    setCommissionValue(raw);
  };

  const resetCalculator = () => {
    setHeroState("input");
    setOfferAmount(0);
  };

  const isValid = commissionValue && parseFloat(commissionValue) > 0 && completionDate;

  const handleOfferClick = () => {
    if (!isValid) {
      setHighlightFields(true);
      setTimeout(() => setHighlightFields(false), 1000);
      return;
    }
    calculateOffer();
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-background">
      {/* Hero image on right side */}
      <div className="absolute top-0 right-0 w-[50%] h-full hidden md:block">
        <img
          src={heroHouse}
          alt="London townhouse with sold sign"
          className="w-full h-full object-cover rounded-bl-[80px]"
          width={960}
          height={1080}
          {...({ fetchpriority: "high" } as any)}
          decoding="async"
        />
        {/* Green overlay for brand feel */}
        <div className="absolute inset-0 bg-primary/20 rounded-bl-[80px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10 py-24 pt-28 md:py-32 md:pt-36">
        <div className="max-w-xl">
          {/* Badges */}
          <div className="flex gap-3 mb-8 animate-fade-in">
            <span className="px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-bold">
              2 Minute Submission
            </span>
            <span className="px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-bold">
              Same Day Offers
            </span>
          </div>

          {/* Main headline - tighter, fewer lines */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-foreground mb-6 leading-[1.05] animate-fade-in tracking-tight">
            Sell your commission.{" "}
            <span className="text-primary">Fast & fair.</span>
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground mb-10 animate-fade-in leading-relaxed max-w-lg font-medium">
            <strong className="text-foreground">Get paid at exchange,</strong> not completion. Enter your upcoming commission below for an instant offer from Agents Capital.
          </p>

          {/* Inline Calculator */}
          {heroState === "input" && (
            <div className="animate-fade-in">
              <div className="flex gap-2 mb-5 max-w-xs">
                <button
                  onClick={() => setUserType("agent")}
                  className={cn(
                    "flex-1 py-3 px-5 rounded-full text-sm font-bold transition-all",
                    userType === "agent"
                      ? "bg-foreground text-background shadow-md"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  )}
                >
                  Agent
                </button>
                <button
                  onClick={() => setUserType("broker")}
                  className={cn(
                    "flex-1 py-3 px-5 rounded-full text-sm font-bold transition-all",
                    userType === "broker"
                      ? "bg-foreground text-background shadow-md"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  )}
                >
                  Broker
                </button>
              </div>

              <div className="space-y-3 max-w-2xl">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <span className="absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-2xl">£</span>
                    <label className="sr-only" htmlFor="commission-input">Commission due (£)</label>
                    <Input
                      id="commission-input"
                      type="text"
                      inputMode="decimal"
                      placeholder="Commission due"
                      value={commissionValue}
                      onChange={handleCommissionChange}
                      className={cn(
                        "pl-14 h-16 text-2xl rounded-2xl border-2 border-border bg-card focus:border-foreground font-bold w-full placeholder:font-normal placeholder:text-muted-foreground/50 transition-shadow",
                        highlightFields && (!commissionValue || parseFloat(commissionValue) <= 0) && "highlight-field border-primary"
                      )}
                    />
                  </div>

                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "justify-start text-left font-semibold h-16 rounded-2xl bg-card border-2 border-border hover:border-foreground sm:min-w-[220px] w-full sm:w-auto text-base transition-shadow",
                          !completionDate && "text-muted-foreground",
                          highlightFields && !completionDate && "highlight-field border-primary"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-5 w-5 shrink-0" />
                        {completionDate ? format(completionDate, "PPP") : "Completion date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Suspense fallback={<div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>}>
                        <Calendar
                          mode="single"
                          selected={completionDate}
                          onSelect={(d) => { setCompletionDate(d); setCalendarOpen(false); }}
                          disabled={(date) => date < addDays(new Date(), 3)}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </Suspense>
                    </PopoverContent>
                  </Popover>
                </div>

                <Button
                  size="lg"
                  onClick={handleOfferClick}
                  className="btn-shimmer bg-primary border-2 border-primary text-primary-foreground hover:bg-primary-hover hover:border-primary-hover font-bold text-lg h-16 rounded-2xl px-10 w-full sm:w-auto cursor-pointer"
                >
                  Get Instant Offer
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>

              <p className="text-sm text-muted-foreground mt-4 font-medium">
                No upfront costs · Instant calculation · No commitment
              </p>
            </div>
          )}

          {heroState === "loading" && (
            <div className="flex items-center gap-4 py-8 animate-fade-in">
              <Loader2 className="h-10 w-10 text-primary animate-spin" />
              <p className="text-muted-foreground text-xl font-semibold">Calculating your offer...</p>
            </div>
          )}

          {heroState === "result" && (
            <div className="animate-fade-in max-w-xl">
              <p className="text-muted-foreground text-lg mb-2 font-medium">We'll buy your commission for</p>
              <div className="text-5xl md:text-7xl font-extrabold text-primary mb-2 tracking-tight">
                £{offerAmount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p className="text-foreground text-2xl font-extrabold mb-8">TODAY</p>
              <p className="text-muted-foreground text-sm -mt-6 mb-8">Net of our fee and VAT (charged at 20% on our fee).</p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link to={`/submit-deal?commissionDue=${encodeURIComponent(commissionValue)}&userType=${userType}`}>
                  <Button size="lg" className="bg-foreground border-2 border-foreground text-background hover:bg-foreground/90 font-bold text-lg h-14 rounded-2xl px-8">
                    Submit This Deal
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="lg" variant="outline" className="font-bold text-lg h-14 rounded-2xl px-8">
                    Register First
                  </Button>
                </Link>
              </div>

              <button onClick={resetCalculator} className="text-muted-foreground hover:text-foreground text-sm underline underline-offset-4 transition-colors mt-4 block font-medium">
                Recalculate
              </button>
            </div>
          )}

          {/* Social Proof Ticker */}
          <div className="mt-8">
            <SocialProofTicker />
          </div>
        </div>
      </div>
    </section>
  );
};
